
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DataRetentionService } from "@/services/analytics/DataRetentionService";
import { analyticsService } from "@/services/analytics/AnalyticsService";
import { Activity, Database, Zap } from "lucide-react";

const AnalyticsInfrastructurePanel = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [retentionStats, setRetentionStats] = useState<any>(null);
  const [bufferStatus, setBufferStatus] = useState<any>(null);

  // Update buffer status every second
  useEffect(() => {
    const updateBufferStatus = () => {
      const status = analyticsService.getBufferStatus();
      setBufferStatus(status);
    };

    updateBufferStatus();
    const interval = setInterval(updateBufferStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleArchiveOldEvents = async () => {
    setIsProcessing(true);
    try {
      const result = await DataRetentionService.archiveOldEvents(6);
      if (result.success) {
        toast.success(`Archived ${result.archivedCount} old events`);
        setRetentionStats(result);
      } else {
        toast.error("Failed to archive events");
      }
    } catch (error) {
      toast.error("Error archiving events");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCleanupSessions = async () => {
    setIsProcessing(true);
    try {
      const result = await DataRetentionService.cleanupOldSessions(12);
      if (result.success) {
        toast.success(`Deleted ${result.deletedCount} old sessions`);
      } else {
        toast.error("Failed to cleanup sessions");
      }
    } catch (error) {
      toast.error("Error cleaning up sessions");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateAggregations = async () => {
    setIsProcessing(true);
    try {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      const result = await DataRetentionService.generateDailyAggregations(yesterday);
      if (result.success) {
        toast.success(`Generated ${result.aggregationCount} aggregation records`);
      } else {
        toast.error("Failed to generate aggregations");
      }
    } catch (error) {
      toast.error("Error generating aggregations");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFlushEvents = () => {
    analyticsService.forceFlush();
    toast.success("Analytics events flushed immediately");
  };

  const getBufferStatusColor = () => {
    if (!bufferStatus) return "secondary";
    if (bufferStatus.bufferSize === 0) return "secondary";
    if (bufferStatus.bufferSize >= bufferStatus.maxBatchSize) return "destructive";
    return "default";
  };

  const getDataLagStatus = () => {
    if (!bufferStatus) return { text: "Unknown", color: "secondary" };
    
    const timeSinceLastFlush = Date.now() - bufferStatus.lastFlush;
    if (timeSinceLastFlush < 10000) return { text: "Real-time", color: "default" };
    if (timeSinceLastFlush < 30000) return { text: "Low lag", color: "secondary" };
    return { text: "High lag", color: "destructive" };
  };

  const lagStatus = getDataLagStatus();

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-card-foreground">
            Analytics Infrastructure Control Panel
            <Badge variant="outline" className="border-border text-foreground">Phase 0</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Real-time Status Panel */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-blue-800 dark:text-blue-200">Real-time Analytics Status</h3>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-700 dark:text-blue-300">Buffer Size:</span>
                <div className="flex items-center gap-2">
                  <Badge variant={getBufferStatusColor()}>
                    {bufferStatus?.bufferSize || 0}/{bufferStatus?.maxBatchSize || 0}
                  </Badge>
                </div>
              </div>
              
              <div>
                <span className="font-medium text-blue-700 dark:text-blue-300">Data Lag:</span>
                <div className="flex items-center gap-2">
                  <Badge variant={lagStatus.color as any}>
                    {lagStatus.text}
                  </Badge>
                </div>
              </div>
              
              <div>
                <span className="font-medium text-blue-700 dark:text-blue-300">Flush Interval:</span>
                <span className="text-blue-600 dark:text-blue-400">
                  {bufferStatus ? `${bufferStatus.flushInterval / 1000}s` : 'Unknown'}
                </span>
              </div>
              
              <div>
                <span className="font-medium text-blue-700 dark:text-blue-300">Admin Mode:</span>
                <Badge variant={bufferStatus?.isAdmin ? "default" : "secondary"}>
                  {bufferStatus?.isAdmin ? "Active" : "Standard"}
                </Badge>
              </div>
            </div>
            
            <div className="mt-3 text-xs text-blue-600 dark:text-blue-400">
              <p>• Admin users get 5-second flush intervals and 2-event batches for real-time analytics</p>
              <p>• Standard users get 10-second intervals and 5-event batches for balanced performance</p>
              <p>• Critical events (saves, creates) trigger immediate flushes</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Event Management */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm text-muted-foreground">Event Management</h3>
              </div>
              <Button 
                onClick={handleFlushEvents}
                variant="outline" 
                size="sm"
                className="w-full"
                disabled={!bufferStatus || bufferStatus.bufferSize === 0}
              >
                Force Flush Buffer ({bufferStatus?.bufferSize || 0} events)
              </Button>
            </div>

            {/* Data Retention */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm text-muted-foreground">Data Retention</h3>
              </div>
              <Button 
                onClick={handleArchiveOldEvents}
                disabled={isProcessing}
                variant="outline" 
                size="sm"
                className="w-full"
              >
                Archive Events (6+ months)
              </Button>
            </div>

            {/* Session Cleanup */}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground">Session Management</h3>
              <Button 
                onClick={handleCleanupSessions}
                disabled={isProcessing}
                variant="outline" 
                size="sm"
                className="w-full"
              >
                Cleanup Old Sessions (12+ months)
              </Button>
            </div>

            {/* Aggregations */}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground">Cost Optimization</h3>
              <Button 
                onClick={handleGenerateAggregations}
                disabled={isProcessing}
                variant="outline" 
                size="sm"
                className="w-full"
              >
                Generate Daily Aggregations
              </Button>
            </div>
          </div>

          {retentionStats && (
            <div className="mt-4 p-3 bg-muted/50 border border-border rounded-lg">
              <h4 className="font-medium text-sm mb-2 text-card-foreground">Latest Retention Stats</h4>
              <p className="text-sm text-muted-foreground">
                Archived: {retentionStats.archivedCount} events
              </p>
            </div>
          )}

          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <h4 className="font-medium text-sm mb-2 text-yellow-800 dark:text-yellow-200">Performance Optimizations</h4>
            <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
              <li>• Real-time subscriptions for dashboard updates</li>
              <li>• Smart event deduplication (2-second window)</li>
              <li>• Adaptive batch sizes based on user role</li>
              <li>• Immediate flush for critical content events</li>
              <li>• Enhanced data validation and error recovery</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsInfrastructurePanel;
